import React, { useState, useReducer } from 'react';
import { Spinner } from '@united-talent-agency/components';

import SortableColumnHeader from '../../../../../components/sortable-column-header/sortable-column-header';
import { getRepresentedByAgencies } from '../../../../../dao/person-agencies';
import { Actions } from './actions';
import { PositionInput, StatusInput, PersonInput, TextInput, TalentAgencyInput, EmphasizeRolePersonCheckbox } from './inputs';
import { sort } from './sorter';
import DataTableLoader from '../../../../../components/content-loader/content-loader';
import { isStatusPersonAssignmentValid } from '../../../../../support/items/statuses';

const AuspicesEntry = ({ role, loadRoles, project, updateRole, projectDeleteRole, algoliaKeys, existingRoles = [], positions, positionLabel = 'Auspice' }) => {
  const [updates, setUpdates] = useState({});
  const [personAgencies, setPersonAgencies] = useState({});

  const archiveAction = role => {
    return () => {
      role.active = !role.active;
      updateRole(role._id, role).then(() => {
        loadRoles();
      });
    };
  };

  const deleteAction = role => {
    return () => {
      projectDeleteRole(role._id).then(() => {
        loadRoles();
      });
    };
  };

  const updateAuspice = (id, field, value) => {
    if (!updates[id]) {
      updates[id] = {};
    }
    if (!updates[id][field]) {
      updates[id][field] = {};
    }
    updates[id][field].value = value;
    setUpdates(Object.assign({}, updates));
  };

  const saveAuspiceFields = () => {
    for (let id in updates) {
      const updatesToSave = {};
      for (let field in updates[id]) {
        if (field === 'errors') {
          continue;
        }
        if (!updates[id].errors) {
          updatesToSave[field] = updates[id][field].value;
        }
      }
      // needed so that, when the update is propagated, we are _guaranteed_ to have all the required info
      // we need both agency and personId in case either of them changes
      //!updatesToSave['agency'] && Object.assign(updatesToSave, { agency: role.agency });
      !updatesToSave['personId'] && Object.assign(updatesToSave, { personId: role.personId });
      return updateRole(id, updatesToSave)
        .then(response => {
          for (let updatedField in updatesToSave) {
            role[updatedField] = response.body[updatedField];
            delete updates[id][updatedField];
          }
          return setUpdates(Object.assign({}, updates));
        })
        .then(() => loadRoles())
        .catch(err => {
          console.error(err);
        });
    }
  };

  const isSaving = (updates, id, field) => {
    return updates[id] && updates[id][field] && updates[id][field].saving;
  };

  const getValue = (updates, role, field) => {
    return updates[role._id] && updates[role._id][field] ? updates[role._id][field].value : role[field];
  };

  const clearError = (updates, role, field, error) => {
    if (updates[role._id] && updates[role._id][field] && updates[role._id][field].error) {
      delete updates[role._id]['status'].errors[error];
    }
  };

  const setError = (updates, role, field, error) => {
    updates[role._id][field].errors = Object.assign(error, updates[role._id][field].errors);
  };

  const getError = (updates, role, field) => {
    return updates[role._id] && updates[role._id][field] && updates[role._id][field].errors;
  };

  const getErrors = (updates, role) => {
    const errors = new Set();
    if (updates[role._id]) {
      for (let field in updates[role._id]) {
        if (updates[role._id][field].errors) {
          for (let error in updates[role._id][field].errors) {
            errors.add(updates[role._id][field].errors[error]);
          }
        }
      }
    }
    return Array.from(errors);
  };

  const validateStatus = (updates, role, field) => {
    const status = getValue(updates, role, 'status');
    const personId = getValue(updates, role, 'personId');
    const isValid = isStatusPersonAssignmentValid(status, personId);
    if (!isValid) {
      const errorMsg = status === 'Open' ? 'Cannot assign person to open position' : 'Person must be assigned';
      setError(updates, role, field, { status: errorMsg });
    } else {
      clearError(updates, role, 'status', 'status');
      clearError(updates, role, 'personId', 'status');
    }
    return isValid;
  };

  const validateDuplicate = (updates, role, field) => {
    const type = getValue(updates, role, 'type');
    const personId = getValue(updates, role, 'personId');
    const existingRole = existingRoles.find(r => {
      return r._id !== role._id && r.type === type && r.personId && personId && r.personId.name === personId.name;
    });
    const isValid = !existingRole;
    if (!isValid) {
      setError(updates, role, field, { type: `Duplicate ${positionLabel}${existingRole.active ? '' : ' (Archived)'}` });
    } else {
      clearError(updates, role, 'type', 'type');
      clearError(updates, role, 'personId', 'type');
    }
    return isValid;
  };

  const validateAndSave = (updates, role, field) => {
    if (updates[role._id] && updates[role._id][field]) {
      const validType = validateDuplicate(updates, role, field);
      const validStatus = validateStatus(updates, role, field);
      const isValid = validType && validStatus;
      if (isValid) {
        updates[role._id][field].saving = true;
        setUpdates(Object.assign({}, updates));
        saveAuspiceFields();
      } else {
        setUpdates(Object.assign({}, updates));
      }
    }
  };

  const getSelectStyles = (baseStyles, updates, role, field, flex) => {
    const retVal = Object.assign({}, baseStyles);
    if (flex) {
      retVal.flex = flex;
    }
    if (getError(updates, role, field)) {
      retVal.controlStyles = Object.assign(Object.assign({}, retVal.controlStyles), { border: '1px solid red' });
    }
    return retVal;
  };

  const positionsEntryClass = role.active ? 'auspices-table-entry' : 'auspices-table-archived-entry';
  const projectType = project.type;
  const backgroundColor = role.active ? 'white' : '#F5F5F5';
  const color = role.active ? 'black' : '#bcbbbc';
  const textInputStyles = {
    flex: '0.18',
    border: '0px',
    outlineWidth: '2px',
    fontSize: '12px',
    paddingTop: '12px',
    fontWeight: 300,
    color,
    backgroundColor,
  };
  const selectInputStyles = {
    controlStyles: {
      minHeight: null,
      border: '0px',
      fontSize: '12px',
    },
    indicatorsContainerStyles: { display: 'none' },
    placeholderStyles: { top: '40%', fontSize: '12px' },
    menuListStyles: { fontSize: '12px' },
    flex: '0.21',
  };
  const errors = getErrors(updates, role);
  const emphasizeRolePersonIsDisabled = (updates, role) => {
    return !getValue(updates, role, 'personId') || getValue(updates, role, 'status') === 'Open' || (getValue(updates, role, 'agency')?.name !== 'UTA' && (getValue(updates, role, 'agency')?.name || getValue(updates, role, 'personId')?.type !== 'Client'));
  };

  return (
    <React.Fragment>
      <div className="auspices-table-entry-container" style={{ backgroundColor, color }}>
        <PositionInput
          onChange={({ value }) => updateAuspice(role._id, 'type', value)}
          type={projectType}
          value={getValue(updates, role, 'type')}
          onBlur={() => validateAndSave(updates, role, 'type')}
          className={positionsEntryClass}
          positions={positions}
          placeholder={positionLabel}
          {...getSelectStyles(selectInputStyles, updates, role, 'type')}
        />
        {isSaving(updates, role._id, 'type') && (
          <div style={{ position: 'relative', left: -30, width: 0, marginTop: 5 }}>
            <Spinner size={20} />
          </div>
        )}
        <StatusInput
          onChange={({ value }) => {
            updateAuspice(role._id, 'status', value);
            if (value === 'Open') {
              updateAuspice(role._id, 'personId', null);
              updateAuspice(role._id, 'agency', null);
              updateAuspice(role._id, 'emphasizeRolePerson', false);
              validateAndSave(updates, role, 'emphasizeRolePerson');
            }
          }}
          type={projectType}
          value={getValue(updates, role, 'status')}
          onBlur={() => validateAndSave(updates, role, 'status')}
          className={positionsEntryClass}
          {...getSelectStyles(selectInputStyles, updates, role, 'status', 0.14)}
        />
        {isSaving(updates, role._id, 'status') && (
          <div style={{ position: 'relative', left: -30, width: 0, marginTop: 5 }}>
            <Spinner size={20} />
          </div>
        )}
        <PersonInput
          onChange={selected => {
            const update = selected.__isNew__
              ? { _id: selected.value, name: selected.value }
              : { _id: selected.value._id, name: selected.value.name };
            updateAuspice(role._id, 'personId', update);
            getRepresentedByAgencies(update).then(personAgencies => {
              if (personAgencies.primary) {
                updateAuspice(role._id, 'agency', personAgencies.primary);
              }
              updateAuspice(role._id, 'emphasizeRolePerson', selected.value?.type === 'Client');
              personAgencies[role._id] = personAgencies.agencies;
              setPersonAgencies(Object.assign({}, personAgencies));
            });
          }}
          hidePlaceholder
          type={projectType}
          value={{
            ...getValue(updates, role, 'personId'),
            emphasizeRolePerson: !emphasizeRolePersonIsDisabled(updates, role) && getValue(updates, role, 'emphasizeRolePerson'),
          }}
          onBlur={() => validateAndSave(updates, role, 'personId')}
          linkClassName={role.active ? 'auspices-table-profile-link' : 'auspices-table-archived-profile-link'}
          {...getSelectStyles(selectInputStyles, updates, role, 'personId')}
        />
        {isSaving(updates, role._id, 'personId') && (
          <div style={{ position: 'relative', left: -30, width: 0, marginTop: 5 }}>
            <Spinner size={20} />
          </div>
        )}
        <TalentAgencyInput
          hidePlaceholder
          value={getValue(updates, role, 'agency')}
          defaultAgencies={personAgencies[role._id]}
          className={positionsEntryClass}
          onChange={change => {
            if (!change) {
              updateAuspice(role._id, 'agency', null);
              updateAuspice(role._id, 'emphasizeRolePerson', getValue(updates, role, 'personId')?.type === 'Client');
              validateAndSave(updates, role, 'emphasizeRolePerson');
              return;
            }
            const { value = '' } = change;
            const update =
              typeof value === 'string' ? { _id: value, name: value } : { _id: value._id, name: value.name };
            updateAuspice(role._id, 'emphasizeRolePerson', (update.name === 'UTA'));
            validateAndSave(updates, role, 'emphasizeRolePerson');
            updateAuspice(role._id, 'agency', update);
          }}
          onBlur={() => validateAndSave(updates, role, 'agency')}
          linkClassName={role.active ? 'auspices-table-profile-link' : 'auspices-table-archived-profile-link'}
          algoliaKeys={algoliaKeys}
          {...getSelectStyles(selectInputStyles, updates, role, 'agency')}
        />
        {isSaving(updates, role._id, 'agency') && (
          <div style={{ position: 'relative', left: -30, width: 0, marginTop: 5 }}>
            <Spinner size={20} />
          </div>
        )}
        <TextInput
          value={getValue(updates, role, 'note')}
          className={positionsEntryClass}
          onChange={value => updateAuspice(role._id, 'note', value)}
          onBlur={() => validateAndSave(updates, role, 'note')}
          styles={textInputStyles}
        />
        {isSaving(updates, role._id, 'note') && (
          <div style={{ position: 'relative', left: -30, width: 0, marginTop: 5 }}>
            <Spinner size={20} />
          </div>
        )}
        <EmphasizeRolePersonCheckbox
          id={role._id}
          disabled={emphasizeRolePersonIsDisabled(updates, role)}
          checked={!emphasizeRolePersonIsDisabled(updates, role) && getValue(updates, role, 'emphasizeRolePerson')}
          onChange={checked => {
            updateAuspice(role._id, 'emphasizeRolePerson', checked);
            validateAndSave(updates, role, 'emphasizeRolePerson');
          }}
          {...getSelectStyles(selectInputStyles, updates, role, 'emphasizeRolePerson', 0.20)}
        />
        {isSaving(updates, role._id, 'emphasizeRolePerson') && (
          <div style={{ position: 'relative', left: -30, width: 0, marginTop: 5 }}>
            <Spinner size={20} />
          </div>
        )}
        <Actions archiveAction={archiveAction(role)} deleteAction={deleteAction(role)} isActive={role.active} itemLabel={positionLabel?.toLowerCase() || 'auspice'} />
      </div>
      {errors.length > 0 && (
        <div className="text-danger auspices-table-inline-duplicate-error">{errors.join('. ')}</div>
      )}
    </React.Fragment>
  );
};

const SortableTableHeader = ({ header, sortBy, changeSort, customStyle = {} }) => (
  <div className="sortable-auspices-table-header" style={customStyle}>
    <SortableColumnHeader
      text={header}
      direction={sortBy.category === header ? (sortBy.direction === 'ASC' ? 1 : -1) : 0}
      onClick={changeSort}
    />
  </div>
);

const UnSortableTableHeader = ({ header, customStyle = {} }) => (
  <div className="unsortable-auspices-table-header" style={{ ...customStyle }}>
    <span>{header}</span>
  </div>
);

export const AuspicesTable = ({ roles, loadRoles, project, updateRole, projectDeleteRole, allRoles, algoliaKeys, positions, positionLabel = 'Auspice' }) => {
  const [sortBy, setSortBy] = useReducer((state, newState) => ({ ...state, ...newState }), {
    direction: 'ASC',
    category: positionLabel,
  });
  const changeSort = newSortCategory => {
    if (newSortCategory !== sortBy.category) {
      sortBy.category = newSortCategory;
    } else {
      sortBy.direction = sortBy.direction === 'ASC' ? 'DSC' : 'ASC';
    }
    setSortBy(sortBy);
  };
  sort(sortBy, roles);
  return (
    <React.Fragment>
      <div className="auspices-table-header-container">
        <SortableTableHeader
          header={positionLabel}
          sortBy={sortBy}
          changeSort={() => changeSort(positionLabel)}
          customStyle={{ flex: '0.21 1 0%' }}
        />
        <SortableTableHeader
          header="Role Status"
          sortBy={sortBy}
          changeSort={() => changeSort('Role Status')}
          customStyle={{ flex: '0.14 1 0%' }}
        />
        <SortableTableHeader
          header="Person"
          sortBy={sortBy}
          changeSort={() => changeSort('Person')}
          customStyle={{ flex: '0.21 1 0%' }}
        />
        <SortableTableHeader
          header="Agency"
          sortBy={sortBy}
          changeSort={() => changeSort('Agency')}
          customStyle={{ flex: '0.21 1 0%' }}
        />
        <UnSortableTableHeader
          header="Notes"
          customStyle={{ flex: '0.18 1 0%' }}
        />
        <UnSortableTableHeader
          header="Bold Client"
          customStyle={{ flex: '0.20 1 0%' }}
        />
        <UnSortableTableHeader
          header="Actions"
          customStyle={{ flex: '0.05 1 0%' }}
        />
      </div>
      {!project?._id && roles?.length === 0 ? (
        <div className="d-flex bg-white">
          {Array.from(Array(6).keys()).map(item => {
            return (
              <div key={item} className="px-2 w-25">
                <DataTableLoader />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="auspices-table">
          {roles &&
            roles.map(role => {
              return (
                <AuspicesEntry
                  role={role}
                  key={role._id}
                  loadRoles={loadRoles}
                  project={project}
                  updateRole={updateRole}
                  projectDeleteRole={projectDeleteRole}
                  existingRoles={allRoles}
                  algoliaKeys={algoliaKeys}
                  positions={positions}
                  positionLabel={positionLabel}
                />
              );
            })}
        </div>
      )}
    </React.Fragment>
  );
};
